import { Config } from 'final-form';
import arrayMutators from 'final-form-arrays';
import { HotelForm as Form } from 'hotel/HotelForm';
import { useCallback, useMemo, useState, useEffect } from 'react';
import { Field } from 'react-final-form';

import { TealButton } from 'components/Button';
import Card from 'components/Card';
import ErrorMessage from 'components/ErrorMessage';
import ButtonRadio from 'components/Inputs/FormInputs/ButtonRadio';
import { ClientTeamMemberSelectOption } from 'components/Inputs/FormInputs/ClientTeamMemberSelect';
import TextField from 'components/Inputs/FormInputs/TextField';
import OrderRequiredFields from 'components/OrderRequiredFields';
import { useClientRequiredFieldsAndPod } from 'hooks/useClientRequiredFieldsAndPod';
import {
  Hotel,
  RoomTypeQuantities,
  RoomConfigurations,
  RoomConfiguration,
} from 'store/groupDetailsSlice';
import { getOrderLevelFields } from 'utilities/guestFieldMapping';
import {
  validateRoomConfigurations,
  validateRoomConfiguration,
} from 'utilities/roomConfigurationHelpers';
import { scrollToFirstError } from 'utilities/scrollToFirstError';

import {
  AmenityRequirement,
  RequiredFieldInput,
} from '../../generated/graphql';
import { required } from '../../utilities/validators';

import GuestProgressIndicator from './GuestProgressIndicator';
import MeetingSpaceRequirements from './MeetingSpaceRequirements';
import { RoomRenderer, DoubleRoomSelection } from './RoomRenderers';

export interface FormValues {
  breakfast: AmenityRequirement;
  freeParking: AmenityRequirement;
  kitchen: AmenityRequirement;
  washerDryer: AmenityRequirement;
  meetingSpace: AmenityRequirement;
  meetingSpaceRequirements?: {
    squareFootage: string;
    avRequirements: string;
    setupDetails?: string;
  };
  otherAmenities: string;
  distanceFromDestination: number;
  brandPreference: string;
  contactEmail: string;
  contactPhone: string;
  suite: (ClientTeamMemberSelectOption | '')[];
  double: (DoubleRoomSelection | '')[];
  single: (ClientTeamMemberSelectOption | '')[];
  quantities: {
    single: number;
    double: number;
    suite: number;
  };
  shortList: Hotel[];
  checkInDate: string;
  checkOutDate: string;
  guestQuantity: string;
  additionalPreferences: string;
  requiredFields: RequiredFieldInput[];
}

export const calculateFilledGuestCount = (values: FormValues): number => {
  let count = 0;

  // Count single room guests
  values.single.forEach((value) => {
    if (value !== '' && value !== undefined) {
      if (typeof value === 'object' && 'guest0' in value) {
        // Multi-guest configuration
        let guestIndex = 0;
        while (value[`guest${guestIndex}` as keyof typeof value]) {
          count++;
          guestIndex++;
        }
      } else {
        count++;
      }
    }
  });

  // Count suite room guests
  values.suite.forEach((value) => {
    if (value !== '' && value !== undefined) {
      if (typeof value === 'object' && 'guest0' in value) {
        // Multi-guest configuration
        let guestIndex = 0;
        while (value[`guest${guestIndex}` as keyof typeof value]) {
          count++;
          guestIndex++;
        }
      } else {
        count++;
      }
    }
  });

  // Count double room guests (firstBed + secondBed)
  values.double.forEach((room) => {
    if (room !== '' && typeof room === 'object' && room !== null) {
      if ('firstBed' in room && room.firstBed) count++;
      if ('secondBed' in room && room.secondBed) count++;
    }
  });

  return count;
};

export const calculateTotalExpectedGuests = (
  roomConfigurations: RoomConfigurations | undefined,
  rooms: RoomTypeQuantities,
): number => {
  if (roomConfigurations) {
    let total = 0;
    roomConfigurations.single.forEach((config) => {
      total += config.rooms * config.guests;
    });
    roomConfigurations.double.forEach((config) => {
      total += config.rooms * config.guests;
    });
    roomConfigurations.suite.forEach((config) => {
      total += config.rooms * config.guests;
    });
    return total;
  }

  // Fallback: assume 1 guest per single/suite, 2 per double
  return rooms.single + rooms.double * 2 + rooms.suite;
};

interface GuestListSectionProps {
  values: FormValues;
  roomConfigurations?: RoomConfigurations;
  rooms: RoomTypeQuantities;
  configurationErrors: string[];
}

const GuestListSection = ({
  values,
  roomConfigurations,
  rooms,
  configurationErrors,
}: GuestListSectionProps) => {
  const filledGuestCount = calculateFilledGuestCount(values);
  const totalExpectedGuests = calculateTotalExpectedGuests(
    roomConfigurations,
    rooms,
  );

  return (
    <div>
      <h2 className="font-oswald text-2xl font-bold uppercase text-secondaryBlue">
        Guest List
      </h2>
      <p className="mt-2 text-base text-gray-600">
        Who&apos;s staying? Assign guests to their rooms for a seamless trip.
      </p>

      {/* Error Messages for Invalid Room Configurations */}
      {configurationErrors.length > 0 && (
        <div className="mt-4 space-y-2">
          {configurationErrors.map((error, index) => (
            <ErrorMessage key={index} errorMessage={error} />
          ))}
        </div>
      )}

      <div className="mt-6">
        <Card
          className="overflow-visible border-0 p-0 sm:border"
          contentClassName="p-0 sm:p-5"
        >
          <GuestProgressIndicator
            filledCount={filledGuestCount}
            totalCount={totalExpectedGuests}
          />
          <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
            {/* Single Rooms */}
            <RoomRenderer
              roomType="single"
              roomConfigurations={roomConfigurations}
              fallbackRoomCount={rooms.single}
            />

            {/* Double Rooms */}
            <RoomRenderer
              roomType="double"
              roomConfigurations={roomConfigurations}
              fallbackRoomCount={rooms.double}
            />

            {/* Suite Rooms */}
            <RoomRenderer
              roomType="suite"
              roomConfigurations={roomConfigurations}
              fallbackRoomCount={rooms.suite}
            />
          </div>
        </Card>
      </div>
    </div>
  );
};

const AmenityRadioButton = ({
  name,
  headerText,
  disableNiceToHave = false,
}: {
  name: string;
  headerText: string;
  disableNiceToHave?: boolean;
}) => (
  <div className="flex w-full flex-col gap-2 border-b border-gray-200 py-4 lg:grid lg:grid-cols-2 lg:items-center lg:gap-0">
    <h3 className="font-arimo text-base font-medium text-gray-900">
      {headerText}
    </h3>
    <div className="flex w-full flex-col gap-2 sm:flex-row sm:gap-4">
      <Field
        type="radio"
        name={name}
        value="NOT_REQUIRED"
        labelText="Not Required"
        component={ButtonRadio}
        className="w-full hover:!bg-transparent focus:!bg-black sm:w-auto sm:hover:!bg-black"
      />
      <Field
        type="radio"
        name={name}
        value="NICE_TO_HAVE"
        labelText="Nice to have"
        component={ButtonRadio}
        className="w-full hover:!bg-transparent focus:!bg-black sm:w-auto sm:hover:!bg-black"
        disabled={disableNiceToHave}
      />
      <Field
        type="radio"
        name={name}
        value="REQUIRED"
        labelText="Required"
        component={ButtonRadio}
        className="w-full hover:!bg-transparent focus:!bg-black sm:w-auto sm:hover:!bg-black"
      />
    </div>
  </div>
);

type GroupRequestForm = {
  onSubmit: Config<FormValues, FormValues>['onSubmit'];
  initialValues: FormValues;
  rooms: RoomTypeQuantities;
  roomConfigurations?: RoomConfigurations;
  hideSubmit?: boolean;
  formId?: string;
};
const GroupRequestForm = ({
  onSubmit,
  initialValues,
  rooms,
  roomConfigurations,
  hideSubmit = false,
  formId,
}: GroupRequestForm) => {
  const showSubmit = !hideSubmit;
  const { clientRequiredFields } = useClientRequiredFieldsAndPod();

  // State for error messages
  const [configurationErrors, setConfigurationErrors] = useState<string[]>([]);

  // Helper function to generate user-friendly error messages
  const generateErrorMessages = useCallback(
    (config: RoomConfigurations | undefined): string[] => {
      if (!config) return [];

      const errors: string[] = [];
      const roomTypes = [
        { type: 'single', configs: config.single },
        { type: 'double', configs: config.double },
        { type: 'suite', configs: config.suite },
      ];

      roomTypes.forEach(({ type, configs }) => {
        if (!validateRoomConfigurations(configs)) {
          configs.forEach((roomConfig: RoomConfiguration, index: number) => {
            if (!validateRoomConfiguration(roomConfig)) {
              if (!Number.isInteger(roomConfig.rooms) || roomConfig.rooms < 0) {
                errors.push(
                  `${type.charAt(0).toUpperCase() + type.slice(1)} room configuration ${index + 1}: Invalid room count (${roomConfig.rooms}). Must be a non-negative whole number.`,
                );
              }
              if (
                !Number.isInteger(roomConfig.guests) ||
                roomConfig.guests < 1 ||
                roomConfig.guests > 2
              ) {
                errors.push(
                  `${type.charAt(0).toUpperCase() + type.slice(1)} room configuration ${index + 1}: Invalid guest count (${roomConfig.guests}). Must be 1 or 2 guests per room.`,
                );
              }
            }
          });
        }
      });

      return errors;
    },
    [],
  );

  // Memoized validation function for room configurations
  const isValidRoomConfiguration = useCallback(
    (config: RoomConfigurations | undefined): boolean => {
      if (!config) return true;

      return (
        validateRoomConfigurations(config.single) &&
        validateRoomConfigurations(config.double) &&
        validateRoomConfigurations(config.suite)
      );
    },
    [],
  );

  // Memoized validation result
  const isConfigurationValid = useMemo(
    () => isValidRoomConfiguration(roomConfigurations),
    [roomConfigurations, isValidRoomConfiguration],
  );

  // Update error messages when room configurations change
  useEffect(() => {
    const errors = generateErrorMessages(roomConfigurations);
    setConfigurationErrors(errors);
  }, [roomConfigurations, generateErrorMessages]);

  const orderFields = getOrderLevelFields(
    clientRequiredFields.map((field) => field.value),
  );
  const hasOrderLevelFields = orderFields.length > 0;

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={initialValues}
      mutators={
        // NOTE: Type assertion required due to complex final-form-arrays generic constraints.
        // arrayMutators provides array field manipulation methods (push, pop, etc.)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- final-form-arrays types are complex
        arrayMutators as Record<string, any>
      }
      render={({ handleSubmit, values, errors, hasValidationErrors }) => {
        // Enhanced submit handler that prevents submission with invalid configurations
        // and scrolls to first error on validation failure
        const enhancedHandleSubmit = (
          event: React.FormEvent<HTMLFormElement>,
        ) => {
          if (!isConfigurationValid) {
            event.preventDefault();
            return;
          }

          // Check for validation errors and scroll to first one
          if (hasValidationErrors && errors) {
            scrollToFirstError(errors as Record<string, unknown>);
          }

          handleSubmit(event);
        };

        return (
          <form onSubmit={enhancedHandleSubmit} id={formId}>
            <div className="space-y-8">
              {/* Amenities Section */}
              <div>
                <h2 className="font-oswald text-2xl font-bold uppercase text-secondaryBlue">
                  Amenities
                </h2>
                <p className="mt-2 text-base text-gray-600">
                  What amenities matter to you?
                </p>
                <div className="mt-6">
                  <AmenityRadioButton name="breakfast" headerText="Breakfast" />
                  <AmenityRadioButton
                    name="freeParking"
                    headerText="Free Parking"
                  />
                  <AmenityRadioButton
                    name="kitchen"
                    headerText="Kitchen or Kitchenette"
                  />
                  <AmenityRadioButton
                    name="washerDryer"
                    headerText="Washer and Dryer"
                  />
                  <AmenityRadioButton
                    name="meetingSpace"
                    headerText="Meeting Space"
                    disableNiceToHave={true}
                  />
                  <MeetingSpaceRequirements
                    showFields={
                      values.meetingSpace === AmenityRequirement.Required
                    }
                  />
                  <div className="flex w-full flex-col gap-4 border-b border-gray-200 py-4 lg:grid lg:grid-cols-2 lg:items-center lg:gap-0">
                    <h3 className="font-arimo text-base font-medium text-gray-900">
                      Are there any other amenities you require?
                    </h3>
                    <div className="w-full">
                      <Field
                        name="otherAmenities"
                        type="text"
                        placeholder="Enter other amenities"
                        component={TextField}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Trip Preferences Section */}
              <div>
                <h2 className="font-oswald text-2xl font-bold uppercase text-secondaryBlue">
                  Trip Preferences
                </h2>
                <p className="mt-2 text-base text-gray-600">
                  Let us know your preferences so we can tailor your trip to
                  your needs.
                </p>
                <div className="mt-6 space-y-6">
                  <div>
                    <h3 className="font-arimo text-base font-medium text-gray-900">
                      How far are you willing to be from your destination? (in
                      miles)
                    </h3>
                    <Field
                      name="distanceFromDestination"
                      type="number"
                      placeholder="Enter distance in miles"
                      component={TextField}
                      min="0"
                      validate={required}
                    />
                  </div>
                  <div>
                    <h3 className="font-arimo text-base font-medium text-gray-900">
                      Do you have a preference for a specific hotel brand?
                    </h3>
                    <Field
                      name="brandPreference"
                      type="text"
                      placeholder="Enter hotel"
                      component={TextField}
                    />
                  </div>
                  <div>
                    <h3 className="font-arimo text-base font-medium text-gray-900">
                      {"Is there anything else you'd like to share?"}
                    </h3>
                    <Field
                      name="additionalPreferences"
                      type="text"
                      placeholder="Enter any additional preferences or requirements"
                      component={TextField}
                    />
                  </div>
                </div>
              </div>

              {/* Reservation Details Section */}
              {hasOrderLevelFields && (
                <div>
                  <h2 className="font-oswald text-2xl font-bold uppercase text-secondaryBlue">
                    Reservation Details
                  </h2>
                  <p className="mt-2 text-base text-gray-600">
                    Provide additional details required for your reservation.
                  </p>
                  <div className="mt-6 space-y-6">
                    <OrderRequiredFields
                      hideHeader={true}
                      removeContainer={true}
                    />
                  </div>
                </div>
              )}

              {/* Contact Details Section */}
              <div>
                <h2 className="font-oswald text-2xl font-bold uppercase text-secondaryBlue">
                  Contact Details
                </h2>
                <p className="mt-2 text-base text-gray-600">
                  Provide your contact details so we can reach you with updates
                  and confirmations.
                </p>
                <div className="mt-6 space-y-6">
                  <div className="flex flex-col gap-6 sm:flex-row">
                    <div className="flex-1">
                      <Field
                        labelText="Email"
                        name="contactEmail"
                        type="email"
                        component={TextField}
                        validate={required}
                      />
                    </div>
                    <div className="flex-1">
                      <Field
                        labelText="Phone"
                        name="contactPhone"
                        type="tel"
                        component={TextField}
                        validate={required}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Guest List Section */}
              <GuestListSection
                values={values}
                roomConfigurations={roomConfigurations}
                rooms={rooms}
                configurationErrors={configurationErrors}
              />
            </div>
            {showSubmit && (
              <div className="mt-8">
                <TealButton
                  text="Submit Request"
                  type="submit"
                  disabled={!isConfigurationValid}
                  className={
                    !isConfigurationValid ? 'cursor-not-allowed opacity-50' : ''
                  }
                />
                {!isConfigurationValid && (
                  <p className="mt-2 text-sm text-red-600">
                    Please fix the room configuration errors above before
                    submitting.
                  </p>
                )}
              </div>
            )}
          </form>
        );
      }}
    />
  );
};

export default GroupRequestForm;
